// src/features/checkpointsSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../axiosInstance'; // Adjust the path as necessary
import { logout } from './authSlice';

// Selector to get unitId from unitsSlice
const selectUnitId = (state) => state.units.unitId;

// Selector to get all selected unit IDs from unitsSlice
const selectSelectedUnitIds = (state) => state.units.selectedUnitIds;

/**
 * Async thunk to fetch checkpoints with request deduplication.
 * Uses `condition` to prevent duplicate in-flight requests.
 * Note: Checkpoints are unit-specific, so we track the last fetched unitIds
 * and refetch when they change.
 */
export const fetchCheckpoints = createAsyncThunk(
  'checkpoints/fetchCheckpoints',
  async (_, { rejectWithValue, getState }) => {
    try {
      const selectedUnitIds = selectSelectedUnitIds(getState());
      const unitId = selectUnitId(getState());

      let response;

      // Fetch all checkpoints (the backend filters statuses by user's accessible units)
      // We'll filter the statuses client-side based on selectedUnitIds
      if (unitId) {
        response = await axiosInstance.get(`/api/checkpoints/?unit_id=${unitId}`);
      } else {
        response = await axiosInstance.get(`/api/checkpoints/`);
      }
      let checkpointsData = response.data;

      // Ensure checkpointsData is an array
      if (!Array.isArray(checkpointsData)) {
        if (checkpointsData.results && Array.isArray(checkpointsData.results)) {
          checkpointsData = checkpointsData.results;
        } else {
          checkpointsData = [checkpointsData];
        }
      }

      // Map API data to the application's expected structure, passing selectedUnitIds
      const mappedCheckpoints = checkpointsData.map((checkpoint) =>
        mapCheckpointFromAPI(checkpoint, selectedUnitIds)
      );

      // Sort checkpoints by due date (ascending order)
      const sortedCheckpoints = [...mappedCheckpoints].sort((a, b) => {
        const dateA = new Date(a.dueDate);
        const dateB = new Date(b.dueDate);
        return dateA - dateB;
      });

      return { checkpoints: sortedCheckpoints, unitIds: selectedUnitIds };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || 'An unknown error occurred.';
      return rejectWithValue(errorMessage);
    }
  },
  {
    // Prevent duplicate requests while one is in-flight
    condition: (_, { getState }) => {
      const { checkpoints } = getState();
      const currentUnitIds = selectSelectedUnitIds(getState());
      
      // If currently loading, don't start another request
      if (checkpoints.status === 'loading') {
        return false;
      }
      
      // If we already have data for these units, don't refetch
      // (unless the units changed - tracked by lastFetchedUnitIds)
      if (checkpoints.status === 'succeeded') {
        const lastIds = checkpoints.lastFetchedUnitIds || [];
        const currentIds = currentUnitIds || [];
        
        // Compare arrays - if they're the same, don't refetch
        if (lastIds.length === currentIds.length && 
            lastIds.every((id, idx) => id === currentIds[idx])) {
          return false;
        }
      }
      
      return true;
    },
  }
);

export const updateCheckpointTitle = createAsyncThunk(
  'checkpoints/updateCheckpointTitle',
  async ({ checkpointId, newTitle }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/api/checkpoints/${checkpointId}/`,
        { checkpoint_name: newTitle }
      );

      return {
        checkpointId,
        newTitle: response.data.checkpoint_name
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || 'An unknown error occurred.';
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to update measure status
export const updateMeasureStatus = createAsyncThunk(
  'checkpoints/updateMeasureStatus',
  async (
    { unitId, checkpointId, statusId, newStatus, newStatusDescription, communicationDate, questionId },
    { rejectWithValue }
  ) => {

    try {
      const payload = {
        status: newStatus,
        status_description: newStatusDescription,
      };
      
      // Add question ID if provided (for question-type checkpoints)
      if (questionId) {
        payload.question = questionId;
        payload.communication_date = communicationDate
      }
      
      const response = await axiosInstance.put(
        `/api/checkpoints/${checkpointId}/statuses/${statusId}/`,
        payload
      );
      
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || 'An unknown error occurred.';
      return rejectWithValue(errorMessage);
    }
  }
);

// New async thunk for sending checkpoint reminders
export const sendCheckpointReminder = createAsyncThunk(
  'checkpoints/sendCheckpointReminder',
  async (checkpointId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/checkpoints/send_reminder/${checkpointId}/`);
      return {
        checkpointId,
        message: response.data.message || 'Reminder sent successfully'
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to send reminder.';
      return rejectWithValue(errorMessage);
    }
  }
);

// Add this new thunk after the other thunks

// Async thunk to fetch default questions with request deduplication
export const fetchDefaultQuestions = createAsyncThunk(
  'checkpoints/fetchDefaultQuestions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/default-questions/');
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to fetch default questions.';
      return rejectWithValue(errorMessage);
    }
  },
  {
    // Prevent duplicate requests - this runs BEFORE the thunk executes
    condition: (_, { getState }) => {
      const { checkpoints } = getState();
      // Only fetch if questionsStatus is 'idle' (never fetched)
      // Skip if already loading or succeeded
      if (checkpoints.questionsStatus === 'loading' || checkpoints.questionsStatus === 'succeeded') {
        return false; // Cancel the thunk - don't execute
      }
      return true; // Proceed with the thunk
    },
  }
);

// **New Async Thunk to Delete a Checkpoint**
export const deleteCheckpoint = createAsyncThunk(
  'checkpoints/deleteCheckpoint',
  async (checkpointId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/checkpoints/${checkpointId}/`);
      return checkpointId;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to delete checkpoint.';
      return rejectWithValue(errorMessage);
    }
  }
);

// New async thunk to update checkpoint date
export const updateCheckpointDate = createAsyncThunk(
  'checkpoints/updateCheckpointDate',
  async ({ unitId, checkpointId, newDate }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/api/checkpoints/${checkpointId}/`,
        { ends_at: newDate }
      );

      return {
        checkpointId,
        newDate: response.data.ends_at
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || 'An unknown error occurred.';
      return rejectWithValue(errorMessage);
    }
  }
);


// Only updating the addCheckpoint thunk - the rest of the file remains unchanged

export const addCheckpoint = createAsyncThunk(
  'checkpoints/addCheckpoint',
  async ({
    unitId,
    checkpointName,
    endsAt,
    checkpointType,
    questionId,
    actionAreaId
  }, { rejectWithValue }) => {
    try {
      const postData = {
        checkpoint_name: checkpointName,
        unit: unitId,
        ends_at: endsAt,
        checkpoint_type: checkpointType
      };

      // Create the checkpoint first
      const response = await axiosInstance.post(`/api/checkpoints/`, postData);
      const createdCheckpoint = response.data;

      // For question type checkpoints, make a second API call to associate the question
      if (checkpointType === 'questions' && questionId) {
        // Create a status for the question
        await axiosInstance.post(`/api/checkpoints/${createdCheckpoint.id}/statuses/`, {
          question: questionId,
          status: "nein" // Default initial status
        });

        // Fetch the updated checkpoint to get the complete data with the question status
        const updatedResponse = await axiosInstance.get(`/api/checkpoints/${createdCheckpoint.id}/`);
        return mapCheckpointFromAPI(updatedResponse.data);
      }
      // For other checkpoint types, just return the created checkpoint
      else if (checkpointType === 'actionareas' && actionAreaId) {
        postData.actionarea_id = actionAreaId;
      }

      return mapCheckpointFromAPI(createdCheckpoint);
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to add checkpoint.';
      return rejectWithValue(errorMessage);
    }
  }
);


// Function to map API data to application data structure
const mapCheckpointFromAPI = (apiData, unitIds) => {
  return {
    id: apiData.id,
    title: apiData.checkpoint_name,
    dueDate: apiData.ends_at,
    checkpointType: apiData.checkpoint_type,
    actionAreas: apiData.checkpoint_type === 'measures'
      ? groupMeasuresByActionArea(apiData.statuses || [], unitIds)
      : apiData.checkpoint_type === 'actionareas'
        ? groupActionAreas(apiData.statuses || [], unitIds)
        : [],
    // Add questions property for question type checkpoints
    questions: apiData.checkpoint_type === 'questions'
      ? groupQuestions(apiData.statuses || [], unitIds)
      : [],
  };
};

const groupQuestions = (statuses, unitIds) => {
  if (!unitIds || !Array.isArray(unitIds) || unitIds.length === 0) {
    return [];
  }
  
  // Filter statuses by the selected units
  return statuses
    .filter(status => status && unitIds.includes(status.unit)) // Include statuses for any selected unit
    .map(status => {
      if (!status) return null;

      return {
        id: status.question.id,                 // Question ID
        question_id: status.question.id,        // More explicit naming
        question_text: status.question?.question_text || status.question_text, // Store the actual question text
        question_tag: status.question?.question_tag || status.question_tag,
        status: status.status,
        status_description: status.status_description,
        status_id: status.id,                // Status ID
        communication_date: status.communication_date,
        unit: status.unit                    // Store the unit ID
      };
    }).filter(item => item !== null);
};

// Function to group measures by their action area
const groupMeasuresByActionArea = (statuses, unitIds) => {
  if (!unitIds || !Array.isArray(unitIds) || unitIds.length === 0) {
    return [];
  }
  
  const actionAreaMap = {};

  // Filter statuses by the selected units - measures now have unit ID directly in the status
  statuses
    .filter(status => status && unitIds.includes(status.unit))
    .forEach((status) => {
      if (!status.measure) return;

      const actionAreaId = status.measure.actionarea;
      const actionAreaTitle = status.measure.actionarea_title || `Action Area ${actionAreaId}`;

      if (!actionAreaMap[actionAreaId]) {
        actionAreaMap[actionAreaId] = {
          id: actionAreaId,
          title: actionAreaTitle,
          measures: [],
        };
      }

      actionAreaMap[actionAreaId].measures.push({
        id: status.measure.id,
        measure_title: status.measure.measure_title,
        responsible_person: status.measure.responsible_person,
        ends_at: status.measure.ends_at,
        progress: status.measure.progress,
        description: status.measure.description,
        actionarea: actionAreaId,
        status: status.status,
        status_description: status.status_description,
        status_id: status.id,
        unit: status.unit  // Store the unit ID from the status
      });
    });

  return Object.values(actionAreaMap);
};

// Update the groupActionAreas function to correctly extract and structure the action area data
const groupActionAreas = (statuses, unitIds) => {
  if (!unitIds || !Array.isArray(unitIds) || unitIds.length === 0) {
    return [];
  }
  
  // Filter statuses by the selected units
  // For actionarea checkpoint types, need to check status.actionarea.unit for filtering
  return statuses
    .filter(status => status && status.actionarea && unitIds.includes(status.actionarea.unit))
    .map(status => {
      if (!status || !status.actionarea) return null;

      // Extract the actionarea data and add the status information to it
      return {
        ...status.actionarea,           // Include all properties from the nested actionarea
        status_id: status.id,           // Include the status ID from the parent
        status: status.status,          // Include the status from the parent
        status_description: status.status_description, // Include the status description
        unit: status.actionarea.unit    // Use unit from actionarea object
      };
    }).filter(item => item !== null);
};

const applyCheckpointStatusUpdate = (checkpoints, updates) => {
  const hasStatus = Object.prototype.hasOwnProperty.call(updates, 'newStatus');
  const hasDescription = Object.prototype.hasOwnProperty.call(updates, 'newStatusDescription');
  const hasCommunicationDate = Object.prototype.hasOwnProperty.call(updates, 'communicationDate');

  checkpoints.forEach((checkpoint) => {
    if (checkpoint.checkpointType === 'questions') {
      checkpoint.questions.forEach((question) => {
        if (question.status_id === updates.statusId) {
          if (hasStatus) {
            question.status = updates.newStatus ?? '';
          }

          if (hasDescription) {
            question.status_description = updates.newStatusDescription ?? '';
          }

          if (hasCommunicationDate) {
            question.communication_date = updates.communicationDate ?? null;
          }
        }
      });
    }

    checkpoint.actionAreas.forEach((actionArea) => {
      if (checkpoint.checkpointType === 'measures') {
        actionArea.measures.forEach((measure) => {
          if (measure.status_id === updates.statusId) {
            if (hasStatus) {
              measure.status = updates.newStatus ?? '';
            }

            if (hasDescription) {
              measure.status_description = updates.newStatusDescription ?? '';
            }
          }
        });
      } else if (checkpoint.checkpointType === 'actionareas' && actionArea.status_id === updates.statusId) {
        if (hasStatus) {
          actionArea.status = updates.newStatus ?? '';
        }

        if (hasDescription) {
          actionArea.status_description = updates.newStatusDescription ?? '';
        }
      }
    });
  });
};

const initialState = {
  checkpoints: [],
  status: 'idle',
  error: null,
  lastFetchedUnitId: null, // Track which unit's checkpoints we have loaded (legacy, for backward compat)
  lastFetchedUnitIds: [], // Track which units' checkpoints we have loaded (for multi-select)
  addStatus: 'idle',
  addError: null,
  deleteStatus: 'idle',
  deleteError: null,
  reminderStatus: 'idle',
  reminderError: null,
  defaultQuestions: [],
  questionsStatus: 'idle',
  questionsError: null,
  sortOrder: 'asc',
};

const checkpointsSlice = createSlice({
  name: 'checkpoints',
  initialState,
  reducers: {
    updateCheckpointStatusLocally: (state, action) => {
      applyCheckpointStatusUpdate(state.checkpoints, action.payload);
    },

    // Add a new reducer to toggle sort order
    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc';

      // Sort checkpoints based on the new sort order
      state.checkpoints.sort((a, b) => {
        const dateA = new Date(a.dueDate);
        const dateB = new Date(b.dueDate);
        return state.sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
    },

    // Add a new reducer to manually sort checkpoints
    sortCheckpoints: (state, action) => {
      const { order } = action.payload;
      state.sortOrder = order;

      state.checkpoints.sort((a, b) => {
        const dateA = new Date(a.dueDate);
        const dateB = new Date(b.dueDate);
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      });
    },

    // Reset checkpoints status to force a refetch
    resetCheckpointsStatus: (state) => {
      state.status = 'idle';
      state.lastFetchedUnitIds = [];
      state.lastFetchedUnitId = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchDefaultQuestions thunk
      .addCase(fetchDefaultQuestions.pending, (state) => {
        state.questionsStatus = 'loading';
        state.questionsError = null;
      })
      .addCase(fetchDefaultQuestions.fulfilled, (state, action) => {
        state.questionsStatus = 'succeeded';
        state.defaultQuestions = action.payload;
      })
      .addCase(fetchDefaultQuestions.rejected, (state, action) => {
        state.questionsStatus = 'failed';
        state.questionsError = action.payload;
      })
      // Handle sendCheckpointReminder thunk
      .addCase(sendCheckpointReminder.pending, (state) => {
        state.reminderStatus = 'loading';
        state.reminderError = null;
      })
      .addCase(sendCheckpointReminder.fulfilled, (state, action) => {
        state.reminderStatus = 'succeeded';
        // Optionally store success message or update UI state
      })
      .addCase(sendCheckpointReminder.rejected, (state, action) => {
        state.reminderStatus = 'failed';
        state.reminderError = action.payload;
      })
      .addCase(updateCheckpointTitle.fulfilled, (state, action) => {
        const { checkpointId, newTitle } = action.payload;
        const checkpointToUpdate = state.checkpoints.find(
          checkpoint => checkpoint.id === checkpointId
        );

        if (checkpointToUpdate) {
          checkpointToUpdate.title = newTitle;
        }
      })
      .addCase(updateCheckpointTitle.rejected, (state, action) => {
        // Handle errors if needed
        state.error = action.payload;
      })
      .addCase(fetchCheckpoints.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCheckpoints.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.checkpoints = action.payload.checkpoints;
        state.lastFetchedUnitIds = action.payload.unitIds || []; // Track which units' data we have
        state.lastFetchedUnitId = action.payload.unitIds?.[0] || null; // Keep for backward compat
      })
      .addCase(fetchCheckpoints.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle updateMeasureStatus thunk
      .addCase(updateMeasureStatus.pending, (state, action) => {
        applyCheckpointStatusUpdate(state.checkpoints, action.meta.arg);
      })
      .addCase(updateMeasureStatus.fulfilled, (state, action) => {
        const updatedStatus = action.payload;
        applyCheckpointStatusUpdate(state.checkpoints, {
          statusId: updatedStatus.id,
          newStatus: updatedStatus.status,
          newStatusDescription: updatedStatus.status_description,
          communicationDate: updatedStatus.communication_date,
        });
      })
      .addCase(updateMeasureStatus.rejected, (state, action) => {
        state.error = action.payload;
        // Optionally, you can set a specific error message or handle it differently
      })
      // Handle updateCheckpointDate thunk
      .addCase(updateCheckpointDate.fulfilled, (state, action) => {
        const { checkpointId, newDate } = action.payload;

        // Find and update the checkpoint's date
        const checkpointToUpdate = state.checkpoints.find(
          checkpoint => checkpoint.id === checkpointId
        );

        if (checkpointToUpdate) {
          checkpointToUpdate.dueDate = newDate;
        }
      })
      .addCase(updateCheckpointDate.rejected, (state, action) => {
        state.error = action.payload;
      })
      // **Handle addCheckpoint Thunk**
      .addCase(addCheckpoint.pending, (state) => {
        state.addStatus = 'loading';
        state.addError = null;
      })
      .addCase(addCheckpoint.fulfilled, (state, action) => {
        state.addStatus = 'succeeded';
        // Append the new checkpoint to the checkpoints array
        state.checkpoints.push(action.payload);
      })
      .addCase(addCheckpoint.rejected, (state, action) => {
        state.addStatus = 'failed';
        state.addError = action.payload;
      })
      // **Handle deleteCheckpoint Thunk**
      .addCase(deleteCheckpoint.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteCheckpoint.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        // Remove the deleted checkpoint from the checkpoints array
        state.checkpoints = state.checkpoints.filter(
          (checkpoint) => checkpoint.id !== action.payload
        );
      })
      .addCase(deleteCheckpoint.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      })
      .addCase(logout, () => initialState);
  },
});

export const {
  updateCheckpointStatusLocally,
  toggleSortOrder,
  sortCheckpoints,
  resetCheckpointsStatus,
} = checkpointsSlice.actions;

export default checkpointsSlice.reducer;
